import { useState } from 'react';
import { Search, Loader2, ExternalLink, FileText, Calendar, X, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApplicants, useUpdateApplicantStatus, useInviteToInterview, useSendBookingLink, useDeleteApplicant } from '../hooks/useApi';
import StatusBadge from '../components/StatusBadge';
import { formatDate } from '../lib/utils';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'responded', label: 'Responded' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'opted_out', label: 'Opted Out' },
];

export default function ApplicantsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [inviteModalId, setInviteModalId] = useState<string | null>(null);
  const [inviteDate, setInviteDate] = useState('');
  const [inviteTime, setInviteTime] = useState('');
  const [inviteDuration, setInviteDuration] = useState(30);
  const [inviteNotes, setInviteNotes] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [bookingLinkSending, setBookingLinkSending] = useState(false);

  const { data, isLoading } = useApplicants(searchQuery, statusFilter, page);
  const updateStatus = useUpdateApplicantStatus();
  const inviteToInterview = useInviteToInterview();
  const sendBookingLink = useSendBookingLink();
  const deleteApplicant = useDeleteApplicant();

  const handleStatusChange = async (applicantId: string, status: string) => {
    await updateStatus.mutateAsync({ applicantId, status });
  };

  const openInviteModal = (applicantId: string) => {
    setInviteModalId(applicantId);
    setInviteDate('');
    setInviteTime('');
    setInviteDuration(30);
    setInviteNotes('');
  };

  const handleSendInvite = async () => {
    if (!inviteModalId || !inviteDate || !inviteTime) return;
    setInviteSending(true);
    try {
      const dateTime = new Date(`${inviteDate}T${inviteTime}`).toISOString();
      await inviteToInterview.mutateAsync({
        applicantId: inviteModalId,
        dateTime,
        duration: inviteDuration,
        notes: inviteNotes || undefined,
      });
      setInviteModalId(null);
    } catch (err) {
      console.error('Failed to send invite:', err);
    } finally {
      setInviteSending(false);
    }
  };

  const handleSendBookingLink = async () => {
    if (!inviteModalId) return;
    setBookingLinkSending(true);
    try {
      await sendBookingLink.mutateAsync(inviteModalId);
      setInviteModalId(null);
    } catch (err) {
      console.error('Failed to send booking link:', err);
    } finally {
      setBookingLinkSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="input pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="input w-full sm:w-48"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Applicant
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Job
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Contact
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Resume
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Applied
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                  </td>
                </tr>
              ) : (
                data?.applicants.map((applicant) => (
                  <tr key={applicant.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {applicant.first_name} {applicant.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{applicant.email}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {applicant.job_title}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {applicant.phone}
                    </td>
                    <td className="py-4 px-4">
                      {applicant.resume_key ? (
                        <Link
                          to={`/conversations/${applicant.id}`}
                          className="inline-flex items-center gap-1 text-green-600 hover:text-green-700"
                        >
                          <FileText className="w-4 h-4" />
                          <span className="text-xs">View</span>
                        </Link>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <StatusBadge status={applicant.status} />
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-500">
                      {formatDate(applicant.created_at)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={applicant.status}
                          onChange={(e) => handleStatusChange(applicant.id, e.target.value)}
                          className="input text-sm py-1.5 w-auto min-w-[100px]"
                        >
                          {STATUS_OPTIONS.filter(o => o.value).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => openInviteModal(applicant.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                          title="Invite to Interview"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                          Invite
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete ${applicant.first_name} ${applicant.last_name}? This will remove all their messages and files. This cannot be undone.`)) {
                              deleteApplicant.mutate(applicant.id);
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          title="Delete applicant"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                        {applicant.resume_url && (
                          <a
                            href={applicant.resume_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-gray-600"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!isLoading && data?.applicants.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500">
                    No applicants found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.applicants.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary text-sm"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={data.applicants.length < 20}
              className="btn-secondary text-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>
      {/* Invite to Interview Modal */}
      {inviteModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setInviteModalId(null)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Invite to Interview</h3>
              <button
                onClick={() => setInviteModalId(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={inviteDate}
                onChange={(e) => setInviteDate(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                value={inviteTime}
                onChange={(e) => setInviteTime(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select
                value={inviteDuration}
                onChange={(e) => setInviteDuration(Number(e.target.value))}
                className="input"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={inviteNotes}
                onChange={(e) => setInviteNotes(e.target.value)}
                className="input resize-none"
                rows={3}
                placeholder="Any additional notes for the candidate..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setInviteModalId(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={!inviteDate || !inviteTime || inviteSending}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                {inviteSending ? 'Sending...' : 'Send Invite'}
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Send Booking Link */}
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-3">
                Let the applicant choose their own time
              </p>
              <button
                onClick={handleSendBookingLink}
                disabled={bookingLinkSending}
                className="btn-secondary inline-flex items-center gap-2 w-full justify-center"
              >
                <Calendar className="w-4 h-4" />
                {bookingLinkSending ? 'Sending...' : 'Send Booking Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
